import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Confirm, Modal, Select, SpinnerOverlay, SubmissionButtons } from '@components/common';

import { useQueryAvailableApprovalUnits } from '@hooks/assets/useQueryAvailableApprovalUnits';
import { useQueryDropdownAssetsAttached } from '@hooks/assets/useQueryDropdownAssetsAttached';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { TAssignmentUnit, TOptionType } from '@interfaces/organisation/assignApprovalUnits';

import { TAssetSubType, TAssetType } from '@enums/map';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';
import { EAssignApprovalUnitsFormNames } from './enums';

import assignUnitSchema from './schema';

import styles from './AssignApprovalUnitsForm.module.scss';

type TAssignmentData = {
	assetTypeId?: string | null;
	assetSubTypeId?: string | null;
	unitIds?: string[] | null;
};

export type TAssignApprovalUnitsFormSubmitHandler = (value: TAssignmentData) => void;

interface IAssignApprovalUnitsFormProps {
	submitHandler: TAssignApprovalUnitsFormSubmitHandler;
	onClose: () => void;
	editAssignment: TAssignmentUnit | null;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	isLoadingSubmit: boolean;
}

interface IAssignFormValues {
	unit: TOptionType<string>[] | null;
	assetType: TOptionType<TAssetType> | null;
	assetSubType: TOptionType<TAssetSubType> | null;
}

const AssignApprovalUnitsForm = ({
	submitHandler,
	onClose,
	editAssignment,
	setIsDirtyFormModal,
	isLoadingSubmit,
}: IAssignApprovalUnitsFormProps) => {
	const [updateAssignData, setUpdateAssignData] = useState<TAssignmentData | undefined>();
	const [isConfirmModal, setIsConfirmModal] = useState(false);

	const handleCloseConfirmModal = () => setIsConfirmModal(false);

	const { availableApprovalUnits, isLoadingAvailableApprovalUnit } = useQueryAvailableApprovalUnits();
	const { dropdownAssetsAttached, isLoadingDropdownAssetsAttached } = useQueryDropdownAssetsAttached();

	const {
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		control,
		watch,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(assignUnitSchema),
		defaultValues: {
			[EAssignApprovalUnitsFormNames.unit]: null,
			[EAssignApprovalUnitsFormNames.assetType]: null,
			[EAssignApprovalUnitsFormNames.assetSubType]: null,
		},
	});

	const isLoadingForm = isLoadingDropdownAssetsAttached || isLoadingAvailableApprovalUnit || isLoadingSubmit;
	const assetTypeValue = watch(EAssignApprovalUnitsFormNames.assetType)?.value;
	const units = watch(EAssignApprovalUnitsFormNames.unit);

	const subTypeByType = assetTypeValue ? dropdownAssetsAttached?.subTypes[assetTypeValue] : [];
	const isActiveSubType = !subTypeByType?.length;

	const updateHandler = () => {
		if (!updateAssignData) return;

		submitHandler(updateAssignData);
		setIsConfirmModal(false);
	};

	const onSubmit = (values: FieldValues) => {
		const { unit, assetType, assetSubType } = values as IAssignFormValues;

		if (!unit?.length) return;

		const unitIds = unit.map((item) => item.value);

		if (!editAssignment) {
			submitHandler({
				unitIds,
				assetTypeId: assetType?.value,
				assetSubTypeId: assetSubType?.value,
			});
		} else {
			setIsConfirmModal(true);
			setUpdateAssignData({
				assetTypeId: editAssignment.assetType.id,
				assetSubTypeId: editAssignment?.assetSubType?.id,
				unitIds: unitIds,
			});
		}
	};

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	useEffect(() => {
		if (editAssignment?.assetType) {
			setValue(
				EAssignApprovalUnitsFormNames.assetType, {
					value: editAssignment.assetType.id,
					label: editAssignment.assetType.assetTypeName,
				});
		}
		if (editAssignment?.assetSubType) {
			setValue(
				EAssignApprovalUnitsFormNames.assetSubType,
				{
					value: editAssignment.assetSubType.id,
					label: editAssignment.assetSubType.assetSubTypeName,
				});
		}
		if (editAssignment?.unitIds) setValue(EAssignApprovalUnitsFormNames.unit, editAssignment.unitIds);
	}, [editAssignment, setValue]);

	useEffect(() => {
		if (isActiveSubType) setValue(EAssignApprovalUnitsFormNames.assetSubType, null);
	}, [isActiveSubType, setValue, assetTypeValue]);

	const changeApprovalUnits = (options: Array<TSelectOption> | unknown) => {
		setValue(EAssignApprovalUnitsFormNames.unit, options);
	};

	return (
		<>
			<Modal
				isOpen={isConfirmModal}
				onClose={handleCloseConfirmModal}
			>
				<Confirm
					onClose={handleCloseConfirmModal}
					onSubmit={updateHandler}
					question='Are you sure you want to update the Approval Unit?'
				/>
			</Modal>
			<div
				className={styles.assignApprovalUnitFormContainer}
				data-testid='assign-approval-unit-form'
			>
				{isLoadingForm && <SpinnerOverlay />}
				<span className={styles.containerHeader}>Asset type</span>
				<div className={styles.assetSelectContainer}>
					<div className={styles.inputContainer}>
						<Select
							control={control}
							disabled={!!editAssignment}
							errorMessage={errors.assetType?.message}
							isClearable
							isError={!!errors?.assetType}
							label='Asset type'
							name={EAssignApprovalUnitsFormNames.assetType}
							options={dropdownAssetsAttached.types}
							placeholder='Choose type'
							required
						/>
					</div>
					<div className={styles.inputContainer}>
						<Select
							control={control}
							disabled={!!editAssignment || isActiveSubType}
							errorMessage={errors.assetSubType?.message}
							isClearable
							isError={!!errors?.assetSubType}
							label='Asset subtype'
							name={EAssignApprovalUnitsFormNames.assetSubType}
							options={subTypeByType}
							placeholder='Choose subtype'
						/>
					</div>
				</div>
				<div className={styles.inputContainer}>
					<Select
						closeMenuOnSelect={false}
						control={control}
						errorMessage={errors.unit?.message}
						handleChange={changeApprovalUnits}
						isClearable
						isError={!!errors?.unit}
						isMulti
						label='Approval units'
						name={EAssignApprovalUnitsFormNames.unit}
						options={availableApprovalUnits}
						placeholder='Choose approval units'
						required
						value={units}
					/>
				</div>
				<SubmissionButtons
					disabledPositive={isLoadingSubmit}
					negativeHandler={onClose}
					positiveHandler={handleSubmit(onSubmit)}
					positiveLabel='Save'
				/>
			</div>
		</>
	);
};

export default AssignApprovalUnitsForm;
